import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/store';
import { useGetConversionPathsQuery } from 'app/apis/queryApi';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { PathsTable } from 'features/conversions/paths/paths-table/paths-table';
import {
  onCalculationTypeChanged,
  onIsIconOnlyToggled,
  selectIsIconOnly,
  selectPathCalculationType
} from 'features/conversions/paths/paths.slice';
import Toggle, { ToggleSize } from 'common/components/inputs/toggle';
import Loading from 'common/components/loading/loading';
import DropDownButtonLegacy from 'common/components/drop-downs/legacy/dropDownButtonLegacy';
import { DropDownItemType } from 'common/components/drop-downs/legacy/dropDownItemType';
import { PathTimeCalculationTypes } from 'features/conversions/paths/paths.models';
import SimpleButtonContainerContent from 'common/components/drop-downs/legacy/simpleDropDownButtonContainerContent';
import { debugToggleByType } from 'features/internal/internal-settings.slice';
import { ToggleType } from 'features/internal/internal.models';

export const Paths: FC = () => {
  const dispatch = useDispatch();
  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const currentPathCalculationType = useSelector(selectPathCalculationType);
  const currencyCode = useSelector(selectCurrencyCode);
  const dates = useSelector(getDateRangeAsObjects);
  const startDate = dates.startDate.toISOString();
  const endDate = dates.endDate.toISOString();
  const isIconOnly = useSelector(selectIsIconOnly);

  const timeToConversionToggle = useSelector((state: RootState) =>
    debugToggleByType(state, ToggleType.DisplayPathsTimeToConversion)
  );

  const showTimeToConversion = timeToConversionToggle?.enabled ?? false;

  const { data: conversionPathsData, isFetching: isFetchingConversionPaths } =
    useGetConversionPathsQuery({
      siteId,
      startDate,
      endDate,
      currencyCode
    });

  const conversionPaths = useMemo(() => {
    if (!isFetchingConversionPaths && conversionPathsData) {
      return conversionPathsData;
    }
    return [];
  }, [conversionPathsData, isFetchingConversionPaths]);

  const calculationTypes: DropDownItemType[] = [
    {
      key: PathTimeCalculationTypes.Mean,
      label: PathTimeCalculationTypes.Mean,
      isActive: false
    },
    {
      key: PathTimeCalculationTypes.Median,
      label: PathTimeCalculationTypes.Median,
      isActive: false
    },
    {
      key: PathTimeCalculationTypes.Mode,
      label: PathTimeCalculationTypes.Mode,
      isActive: false
    }
  ];

  return (
    <>
      <div className=" mb-3 flex justify-between items-center">
        <Toggle
          identifier="show-icons-only"
          label="Icons only"
          size={ToggleSize.Small}
          onChange={() => dispatch(onIsIconOnlyToggled())}
          checked={isIconOnly}
        />

        <DropDownButtonLegacy
          dropDownItems={calculationTypes}
          selectDropDownItem={(calculationType) => {
            dispatch(
              onCalculationTypeChanged(
                calculationType as PathTimeCalculationTypes
              )
            );
          }}
          dropDirection={'right'}
        >
          {/* Hiding this for now as could be confusing  */}
          <SimpleButtonContainerContent
            dropdownLabel={`Time To Conversion Calculation: ${currentPathCalculationType}`}
            additionalContainerStyling={`${
              showTimeToConversion ? '' : 'hidden'
            } bg-white rounded border border-neutral-200 w-80`}
          />
        </DropDownButtonLegacy>
      </div>
      <section className="">
        {isFetchingConversionPaths ? (
          <div className="flex justify-center">
            <Loading />
          </div>
        ) : (
          <>
            {conversionPaths.length > 0 ? (
              <div className="">
                <PathsTable conversionPaths={conversionPaths} />
              </div>
            ) : (
              <div>
                <i className="ra-icons-generic-spanner-wrench text-neutral-500 text-4xl text-center block"></i>
                <h2 className="text-center text-xl">
                  Paths to Conversions cannot be generated
                </h2>
                <p className="text-center mt-1 text-sm">
                  Please review your
                  <a
                    href="https://ruler-documentation.readme.io/docs/form-tracking"
                    className="text-primary-100"
                  >
                    {' '}
                    Conversion Tracking Setup
                  </a>
                </p>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
};
